import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useAuth } from '@/components/auth/AuthProvider';
import useApi from '@/hooks/useApi';
import {
  getLikedCandidateIds,
  replaceLikedCandidates,
  subscribeLikedCandidates,
} from '@/hooks/useLikedCandidates';
import {
  getMeasureSelections,
  replaceMeasureSelections,
  subscribeMeasureSelections,
} from '@/hooks/useMeasureSelections';
import { BallotRecord, BallotRecordSummary } from '@/types/ballotRecords';
import { ContextService } from '@/services/contextService';
import { filterOnBallotElections } from '@/utils/ballotStatus';
import {
  clearDraftSelections,
  getDraftSelections,
  pruneDraftSelections,
  setDraftSelections,
} from '@/utils/draftSelectionsStore';
import {
  clearDraftMeasureSelections,
  getDraftMeasureSelections,
  pruneDraftMeasureSelections,
  setDraftMeasureSelections,
} from '@/utils/draftMeasureSelectionsStore';

const SELECTIONS_PUT_DEBOUNCE_MS = 800;

interface BallotRecordsContextValue {
  /** Summaries from GET /user/ballots, newest election first. */
  records: BallotRecordSummary[];
  recordsLoading: boolean;
  refreshRecords: () => Promise<void>;
  /** The active ballot selection (one (address, election) record). Drives the
   * live view, the settings-displayed address, and the chat context. */
  activeBallotId: string | null;
  /** Make a ballot the active selection (POST /activate): syncs the current
   * address to it and re-points the chat context. */
  activateBallot: (id: string) => Promise<boolean>;
  /** The active ballot's summary, if loaded. */
  currentRecord: BallotRecordSummary | null;
  /** The active ballot's FULL record (elections + sibling tabs), for the live
   * ballot view. Fetched when the active selection changes. */
  activeRecord: BallotRecord | null;
  /** True when the active record's fetch genuinely failed (not merely
   * in-flight). Lets the view surface an error instead of an endless skeleton. */
  activeRecordError: boolean;
  /** True while a first ballot is being built for the current address (signup or a
   * landing address submit). Consumers suppress the landing and show a skeleton. */
  building: boolean;
  /** Enter the building state immediately for a signed-in landing address submit,
   * before the (slow) settings write + records refresh resolve. */
  beginBuilding: () => void;
  /** Full record being viewed from history; null = the live current ballot. */
  viewedRecord: BallotRecord | null;
  viewedLoading: boolean;
  viewedError: boolean;
  viewRecord: (id: string) => Promise<void>;
  clearViewedRecord: () => void;
  saveRecord: (id: string) => Promise<boolean>;
  /** Toggle the per-ballot "Complete this ballot" override (green complete
   * state regardless of picks; commits to saved when enabled). */
  setManualCompletion: (id: string, complete: boolean) => Promise<boolean>;
  deleteRecord: (id: string) => Promise<boolean>;
  updateViewedSelections: (
    candidates: string[],
    measures?: Record<string, string>
  ) => Promise<void>;
  /** null until settings load. */
  autoSaveEnabled: boolean | null;
  setAutoSaveEnabled: (enabled: boolean) => Promise<boolean>;
  /** Sync the shared auto-save value from a save that already persisted it
   * elsewhere (e.g. the Settings page POST) — updates local state only, no
   * second POST, so other consumers (the ballot status popover) reflect the
   * change immediately. */
  syncAutoSaveEnabled: (enabled: boolean) => void;
}

const noopAsync = async () => {};

const defaultValue: BallotRecordsContextValue = {
  records: [],
  recordsLoading: false,
  refreshRecords: noopAsync,
  activeBallotId: null,
  activateBallot: async () => false,
  currentRecord: null,
  activeRecord: null,
  activeRecordError: false,
  building: false,
  beginBuilding: () => {},
  viewedRecord: null,
  viewedLoading: false,
  viewedError: false,
  viewRecord: noopAsync,
  clearViewedRecord: () => {},
  saveRecord: async () => false,
  setManualCompletion: async () => false,
  deleteRecord: async () => false,
  updateViewedSelections: noopAsync,
  autoSaveEnabled: null,
  setAutoSaveEnabled: async () => false,
  syncAutoSaveEnabled: () => {},
};

const BallotRecordsContext = createContext<BallotRecordsContextValue>(defaultValue);

export const useBallotRecords = (): BallotRecordsContextValue => useContext(BallotRecordsContext);

/** A record as the API returned it, holding only what is on the ballot. Every
 *  `/user/ballots` response passes through here, so a `withdrawn` candidate or
 *  `notOnBallot` measure from an older backend never renders, counts toward
 *  completeness, or keeps a pick alive. */
const parseRecord = (raw: unknown): BallotRecord => filterOnBallotElections(raw as BallotRecord);

/** Merge a PUT/PATCH response record back into the summary list. */
const mergeIntoSummaries = (
  summaries: BallotRecordSummary[],
  record: BallotRecord
): BallotRecordSummary[] =>
  summaries.map((s) =>
    s.id === record.id
      ? {
          ...s,
          status: record.status,
          saved_at: record.saved_at ?? s.saved_at,
          heart_count: record.selections?.candidates?.length ?? s.heart_count,
          // Backend-computed on every write; keep the prior value if a response omits it.
          is_complete: record.is_complete ?? s.is_complete,
          manually_complete: record.manually_complete ?? s.manually_complete,
        }
      : s
  );

// Candidate ids across a record's contests — distinguishes its own picks from
// local-only hearts (e.g. national-view picks not stored in any record).
const candidateIdsInRecord = (record: BallotRecord): Set<string> => {
  const ids = new Set<string>();
  for (const election of record.elections ?? []) {
    const contests =
      (election as { contests?: Array<{ candidates?: Array<{ id?: string }> }> }).contests ?? [];
    for (const contest of contests) {
      for (const c of contest.candidates ?? []) {
        if (c.id) ids.add(c.id);
      }
    }
  }
  return ids;
};

// Same idea for measure ids — distinguishes this record's own measures from
// local-only votes on a measure that isn't part of this ballot.
const measureIdsInRecord = (record: BallotRecord): Set<string> => {
  const ids = new Set<string>();
  for (const election of record.elections ?? []) {
    const measures =
      (election as { ballot_measures?: Array<{ id?: string }> }).ballot_measures ?? [];
    for (const m of measures) {
      if (m.id) ids.add(m.id);
    }
  }
  return ids;
};

export const BallotRecordsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, user, getAccessTokenSilently } = useAuth();
  const query = useApi();
  const enabled = isAuthenticated && Boolean(user?.initialSetupComplete);

  const [records, setRecords] = useState<BallotRecordSummary[]>([]);
  const [activeBallotId, setActiveBallotId] = useState<string | null>(null);
  const [activeRecord, setActiveRecord] = useState<BallotRecord | null>(null);
  const [activeRecordError, setActiveRecordError] = useState(false);
  const [recordsLoading, setRecordsLoading] = useState(false);
  // True while a first ballot is being built for the current address (signup or
  // a landing address submit): keeps the skeleton up and the landing suppressed
  // until the background DW population + fan-out lands a record. Cleared by the
  // poll below (or refreshRecords) once a record appears, or on timeout.
  const [building, setBuilding] = useState(false);

  // Enter the building state immediately for a landing address submit, before the
  // settings write + records refresh resolve.
  const beginBuilding = useCallback(() => setBuilding(true), []);
  const [viewedRecord, setViewedRecord] = useState<BallotRecord | null>(null);
  const [viewedLoading, setViewedLoading] = useState(false);
  const [viewedError, setViewedError] = useState(false);
  const [autoSaveEnabled, setAutoSave] = useState<boolean | null>(null);

  // Full-record cache (id → BallotRecord) so activating a previously-loaded
  // ballot renders instantly while the fresh copy hydrates in the background.
  const recordCacheRef = useRef<Map<string, BallotRecord>>(new Map());
  // Monotonic activation token so only the latest activate reconciles state.
  const activateSeqRef = useRef(0);

  // Selection-sync bookkeeping for the CURRENT record. Refs (not state):
  // the debounced PUT loop reads them without re-subscribing.
  const currentIdRef = useRef<string | null>(null);
  // The active record's status + its candidate/measure ids, read by the sync
  // effect to decide where picks go: a draft's stay on-device, a saved
  // ballot's go to Cosmos.
  const currentStatusRef = useRef<'draft' | 'saved' | null>(null);
  const currentBallotIdsRef = useRef<Set<string>>(new Set());
  const currentBallotMeasureIdsRef = useRef<Set<string>>(new Set());
  // Auto-save preference, read by the sync effect: off → edits stay on-device
  // and revert the ballot to draft; on → edits mirror straight to Cosmos.
  const autoSaveRef = useRef(false);
  const applyingServerRef = useRef(false);
  // True while a newly-current record hydrates: suppresses selection PUTs during
  // the handoff so the shared hearts store (still holding the previous ballot's
  // candidates) can't contaminate the new ballot on an address switch.
  const hydratingRef = useRef(false);
  // Monotonic hydration token: only the latest bind owns the gate, so a
  // superseded run can't leave it stuck true and drop every heart PUT.
  const hydrateTokenRef = useRef(0);
  const putTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingPutRef = useRef<{
    recordId: string;
    candidates: string[];
    measures: Record<string, string>;
  } | null>(null);

  const putSelections = useCallback(
    async (recordId: string, candidates: string[], measures: Record<string, string>) => {
      const response = await query(`/user/ballots/${recordId}/selections`, {
        method: 'PUT',
        body: JSON.stringify({ candidates, measures }),
      });
      const data = await response.json();
      if (data.success && data.ballot) {
        const ballot = parseRecord(data.ballot);
        setRecords((prev) => mergeIntoSummaries(prev, ballot));
        return ballot;
      }
      return null;
    },
    [query]
  );

  const refreshRecords = useCallback(async () => {
    if (!enabled) return;
    setRecordsLoading(true);
    try {
      const response = await query('/user/ballots', { method: 'GET' });
      const data = await response.json();
      if (data.success && Array.isArray(data.ballots)) {
        const ballots = data.ballots as BallotRecordSummary[];
        setRecords(ballots);
        setActiveBallotId((data.active_ballot_id as string | null) ?? null);
        // A record is in hand → the build (if any) is done.
        if (ballots.length > 0) setBuilding(false);
        // Drop on-device draft picks for ballots that no longer exist.
        pruneDraftSelections(ballots.map((b) => b.id));
        pruneDraftMeasureSelections(ballots.map((b) => b.id));
      }
    } catch (error) {
      console.error('Ballot records fetch failed:', error);
    } finally {
      setRecordsLoading(false);
    }
  }, [enabled, query]);

  // Tracks the last-seen initialSetupComplete so the reset effect can spot the
  // false→true flip that means signup just finished.
  const prevSetupCompleteRef = useRef<boolean | undefined>(undefined);

  // Poll for the first record while building. Bounded so a genuine failure (no
  // ballot ever lands) falls through to the landing instead of an endless skeleton.
  useEffect(() => {
    if (!enabled || !building) return;
    let cancelled = false;
    let attempts = 0;
    let timer: ReturnType<typeof setTimeout>;
    const tick = async () => {
      if (cancelled) return;
      attempts += 1;
      try {
        const response = await query('/user/ballots', { method: 'GET' });
        const data = await response.json();
        if (!cancelled && data.success && Array.isArray(data.ballots) && data.ballots.length > 0) {
          const ballots = data.ballots as BallotRecordSummary[];
          setRecords(ballots);
          setActiveBallotId((data.active_ballot_id as string | null) ?? null);
          setBuilding(false);
          return;
        }
      } catch {
        // Transient — keep polling until the cap.
      }
      if (!cancelled) {
        if (attempts >= 20) {
          setBuilding(false); // ~30s cap → give up, fall through to landing
          return;
        }
        timer = setTimeout(tick, 1500);
      }
    };
    timer = setTimeout(tick, 600);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [enabled, building, query]);

  // Initial load + reset across sign-in/sign-out. Also owns the "building"
  // decision: when setup flips complete while already signed in, signup just
  // finished and the ballot is being built in a background task — enter the
  // building state so the poll above surfaces the first record instead of the
  // view flashing the landing. (Decided here, not in a separate effect, so the
  // reset below can't clear it on the same render.)
  useEffect(() => {
    const hadUser = isAuthenticated && Boolean(user);
    const wasComplete = prevSetupCompleteRef.current;
    const isComplete = Boolean(user?.initialSetupComplete);
    // Only a genuine authed setup-incomplete → setup-complete flip is a signup.
    // Leave the ref undefined until we have a real user (and reset it on sign-out)
    // so the first load of a returning user — including one with zero ballots —
    // is never mistaken for a signup (which would spin the skeleton to timeout).
    const justSignedUp = hadUser && wasComplete === false && isComplete;
    prevSetupCompleteRef.current = hadUser ? isComplete : undefined;

    setRecords([]);
    setActiveBallotId(null);
    setActiveRecord(null);
    setActiveRecordError(false);
    setBuilding(justSignedUp);
    setViewedRecord(null);
    setAutoSave(null);
    currentIdRef.current = null;
    if (!enabled) return;
    void refreshRecords();
    void (async () => {
      try {
        const response = await query('/user/settings', { method: 'GET' });
        const data = await response.json();
        if (data.success && data.data) {
          setAutoSave(Boolean(data.data.auto_save_ballots));
        }
      } catch (error) {
        console.error('Settings fetch failed:', error);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, user?.user_id]);

  // The active record drives the live view + hearts binding. Every per-election
  // record at the current address carries is_current_address=true, so we key on
  // the active pointer; fall back to the current address's visible entry until
  // the pointer loads (or for legacy users without one).
  const currentRecord = useMemo(() => {
    if (activeBallotId) {
      const active = records.find((r) => r.id === activeBallotId);
      if (active) return active;
    }
    return records.find((r) => r.is_current_address && r.is_visible_entry !== false) ?? null;
  }, [records, activeBallotId]);

  // Track the active record's status for the sync effect so a save (status flip
  // without an id change) redirects picks from the local draft cache to Cosmos.
  useEffect(() => {
    currentStatusRef.current = currentRecord?.status ?? null;
  }, [currentRecord?.status]);

  useEffect(() => {
    autoSaveRef.current = autoSaveEnabled === true;
  }, [autoSaveEnabled]);

  // Left-menu order: newest ballot first, keyed on ISO ``createdAt`` (records
  // without one fall to the bottom). Opening an older ballot doesn't reorder.
  const orderedRecords = useMemo(
    () => [...records].sort((a, b) => (b.createdAt ?? '').localeCompare(a.createdAt ?? '')),
    [records]
  );

  // Bind hearts to the current record: hydrate once from the server, then
  // mirror every local change up via a debounced full-replace PUT.
  useEffect(() => {
    if (!enabled || !currentRecord) return;
    if (currentIdRef.current === currentRecord.id) return;

    // Rebinding to a different record (address change): flush any PUT still
    // pending against the previous record NOW, so it can't fire later with
    // the old hearts against the new record id.
    if (putTimerRef.current) {
      clearTimeout(putTimerRef.current);
      putTimerRef.current = null;
    }
    const pending = pendingPutRef.current;
    pendingPutRef.current = null;
    if (pending) {
      void putSelections(pending.recordId, pending.candidates, pending.measures).catch((error) =>
        console.error('Ballot selections flush failed:', error)
      );
    }

    const isFirstBind = currentIdRef.current === null;
    currentIdRef.current = currentRecord.id;

    // Hold selection PUTs off until this record's server state loads, so a
    // heart toggled mid-handoff can't push the old ballot's candidates onto the
    // new one. Ownership uses a token, not a cleanup flag: a same-id refresh
    // re-runs this effect and early-returns above without a new hydration, so a
    // cleanup ``cancelled`` flag would leave the gate stuck true. The in-flight
    // run keeps ownership; only a different-id rebind bumps the token.
    hydratingRef.current = true;
    const token = ++hydrateTokenRef.current;
    setActiveRecordError(false);
    void (async () => {
      try {
        const response = await query(`/user/ballots/${currentRecord.id}`, { method: 'GET' });
        const data = await response.json();
        // A newer bind superseded this one — stay silent, it owns the state now.
        if (hydrateTokenRef.current !== token) return;
        // The fetch resolved but carried no usable record → a genuine failure,
        // surface it so the view shows an error rather than an endless skeleton.
        if (!data.success || !data.ballot) {
          setActiveRecordError(true);
          return;
        }
        const record = parseRecord(data.ballot);
        // Surface the full active record (elections + sibling tabs) for the live view.
        setActiveRecord(record);
        recordCacheRef.current.set(record.id, record);
        currentStatusRef.current = record.status ?? 'draft';
        const ballotIds = candidateIdsInRecord(record);
        currentBallotIdsRef.current = ballotIds;
        const ballotMeasureIds = measureIdsInRecord(record);
        currentBallotMeasureIdsRef.current = ballotMeasureIds;
        const localIds = getLikedCandidateIds();
        const localMeasures = getMeasureSelections();
        // Local-only picks (e.g. national-view) not tied to this ballot — kept
        // across a refresh so they survive the reload.
        const localOnly = localIds.filter((id) => !ballotIds.has(id));
        const localMeasuresOnly = Object.fromEntries(
          Object.entries(localMeasures).filter(([mid]) => !ballotMeasureIds.has(mid))
        );

        if (record.status === 'saved') {
          // Saved: Cosmos selections are authoritative.
          const serverIds = record.selections?.candidates ?? [];
          const serverMeasures = record.selections?.measures ?? {};
          if (serverIds.length || Object.keys(serverMeasures).length) {
            applyingServerRef.current = true;
            replaceLikedCandidates(
              isFirstBind ? Array.from(new Set([...serverIds, ...localOnly])) : serverIds
            );
            replaceMeasureSelections(
              isFirstBind ? { ...localMeasuresOnly, ...serverMeasures } : serverMeasures
            );
            applyingServerRef.current = false;
          } else if (isFirstBind && (localIds.length || Object.keys(localMeasures).length)) {
            // First sync after sign-in into a saved ballot: carry guest hearts/votes to Cosmos.
            await putSelections(
              record.id,
              localIds.filter((id) => ballotIds.has(id)),
              Object.fromEntries(
                Object.entries(localMeasures).filter(([mid]) => ballotMeasureIds.has(mid))
              )
            );
          } else if (!isFirstBind) {
            applyingServerRef.current = true;
            replaceLikedCandidates([]);
            replaceMeasureSelections({});
            applyingServerRef.current = false;
          }
        } else {
          // Draft: picks live on-device (per ballot). Fall back to the last-saved
          // Cosmos selections when this device has no working copy (e.g. a ballot
          // reverted to draft on another device); an empty cache (edited to zero)
          // is respected, not overridden.
          const cached = getDraftSelections(record.id);
          let draftIds = cached ?? record.selections?.candidates ?? [];
          if (isFirstBind && cached === null && !draftIds.length && localIds.length) {
            // First sign-in with guest hearts into a fresh draft: seed the cache.
            draftIds = localIds.filter((id) => ballotIds.has(id));
            setDraftSelections(record.id, draftIds);
          }
          const cachedMeasures = getDraftMeasureSelections(record.id);
          let draftMeasures = cachedMeasures ?? record.selections?.measures ?? {};
          if (
            isFirstBind &&
            cachedMeasures === null &&
            !Object.keys(draftMeasures).length &&
            Object.keys(localMeasures).length
          ) {
            draftMeasures = Object.fromEntries(
              Object.entries(localMeasures).filter(([mid]) => ballotMeasureIds.has(mid))
            );
            setDraftMeasureSelections(record.id, draftMeasures);
          }
          applyingServerRef.current = true;
          replaceLikedCandidates(
            isFirstBind ? Array.from(new Set([...draftIds, ...localOnly])) : draftIds
          );
          replaceMeasureSelections(
            isFirstBind ? { ...localMeasuresOnly, ...draftMeasures } : draftMeasures
          );
          applyingServerRef.current = false;
        }
      } catch (error) {
        if (hydrateTokenRef.current === token) {
          console.error('Ballot selections hydrate failed:', error);
          setActiveRecordError(true);
        }
      } finally {
        // Only the latest hydration clears the gate it set.
        if (hydrateTokenRef.current === token) hydratingRef.current = false;
      }
    })();
  }, [enabled, currentRecord, query, putSelections]);

  // Revert a saved ballot to draft after an edit made with auto-save off (its
  // picks now live on-device until the next save). Idempotent for drafts.
  const revertToDraft = useCallback(
    async (id: string) => {
      try {
        const response = await query(`/user/ballots/${id}`, {
          method: 'PATCH',
          body: JSON.stringify({ status: 'draft' }),
        });
        const data = await response.json();
        if (data.success && data.ballot) {
          const ballot = parseRecord(data.ballot);
          setRecords((prev) => mergeIntoSummaries(prev, ballot));
          setActiveRecord((prev) =>
            prev?.id === id ? { ...prev, status: 'draft', saved_at: null } : prev
          );
        }
      } catch (error) {
        console.error('Ballot revert-to-draft failed:', error);
      }
    },
    [query]
  );

  useEffect(() => {
    if (!enabled) return;
    const scheduleSync = () => {
      if (applyingServerRef.current || hydratingRef.current || !currentIdRef.current) return;
      const recordId = currentIdRef.current;
      const candidates = getLikedCandidateIds();
      const measures = getMeasureSelections();

      if (!autoSaveRef.current) {
        // Auto-save OFF: every edit keeps this ballot's picks on-device (grounded
        // to its own contests so national-view picks don't leak in) and reverts a
        // saved ballot to draft — the user re-commits with Save (or auto-save on).
        const ballotIds = currentBallotIdsRef.current;
        setDraftSelections(
          recordId,
          ballotIds.size ? candidates.filter((id) => ballotIds.has(id)) : candidates
        );
        const ballotMeasureIds = currentBallotMeasureIdsRef.current;
        setDraftMeasureSelections(
          recordId,
          ballotMeasureIds.size
            ? Object.fromEntries(
                Object.entries(measures).filter(([mid]) => ballotMeasureIds.has(mid))
              )
            : measures
        );
        if (currentStatusRef.current === 'saved') {
          currentStatusRef.current = 'draft';
          void revertToDraft(recordId);
        }
        return;
      }

      // Auto-save ON: the ballot is saved → mirror to Cosmos via a debounced
      // full-replace PUT. Capture the payload at schedule time — an address change
      // can rebind currentIdRef before the debounce fires.
      const payload = { recordId, candidates, measures };
      pendingPutRef.current = payload;
      if (putTimerRef.current) clearTimeout(putTimerRef.current);
      putTimerRef.current = setTimeout(() => {
        putTimerRef.current = null;
        if (pendingPutRef.current !== payload) return; // flushed or superseded
        pendingPutRef.current = null;
        void putSelections(payload.recordId, payload.candidates, payload.measures).catch((error) =>
          console.error('Ballot selections sync failed:', error)
        );
      }, SELECTIONS_PUT_DEBOUNCE_MS);
    };
    const unsubscribeCandidates = subscribeLikedCandidates(scheduleSync);
    const unsubscribeMeasures = subscribeMeasureSelections(scheduleSync);
    return () => {
      unsubscribeCandidates();
      unsubscribeMeasures();
      if (putTimerRef.current) clearTimeout(putTimerRef.current);
    };
  }, [enabled, putSelections, revertToDraft]);

  const viewRecord = useCallback(
    async (id: string) => {
      if (!enabled) return;
      if (currentIdRef.current === id) {
        // The live ballot already renders the current record.
        setViewedRecord(null);
        setViewedError(false);
        return;
      }
      setViewedLoading(true);
      setViewedError(false);
      try {
        const response = await query(`/user/ballots/${id}`, { method: 'GET' });
        const data = await response.json();
        if (data.success && data.ballot) {
          const record = parseRecord(data.ballot);
          recordCacheRef.current.set(record.id, record);
          setViewedRecord(record);
        } else {
          setViewedError(true);
        }
      } catch (error) {
        console.error('Ballot record fetch failed:', error);
        setViewedError(true);
      } finally {
        setViewedLoading(false);
      }
    },
    [enabled, query]
  );

  const clearViewedRecord = useCallback(() => {
    setViewedRecord(null);
    setViewedError(false);
  }, []);

  // Chat context follows the active ballot, so re-init it after the selection
  // changes — the new-chat suggestion chips and agent prompt refresh even when
  // the ballot view isn't mounted. Best-effort; chips also update on the next
  // context event.
  const reinitContext = useCallback(async () => {
    try {
      const token = await getAccessTokenSilently();
      await ContextService.initContext(token);
    } catch {
      // ignore — non-fatal for the write that just succeeded
    }
  }, [getAccessTokenSilently]);

  const activateBallot = useCallback(
    async (id: string): Promise<boolean> => {
      if (!enabled) return false;
      // Optimistic: move the selection now so the switch feels instant. A cached
      // full record renders immediately; otherwise the hydration effect (keyed on
      // the active id) fetches it and shows a skeleton meanwhile. The POST only
      // persists the pointer + refreshes the list/chat-context — off the render path.
      const seq = ++activateSeqRef.current;
      setViewedRecord(null);
      setViewedError(false);
      setActiveRecord(recordCacheRef.current.get(id) ?? null);
      setActiveBallotId(id);
      void (async () => {
        try {
          const response = await query(`/user/ballots/${id}/activate`, { method: 'POST' });
          const data = await response.json();
          if (data?.success && data.ballot) {
            recordCacheRef.current.set(id, parseRecord(data.ballot));
          }
        } catch (error) {
          console.error('Ballot activate failed:', error);
        }
        // Only the latest activation reconciles the list + chat context, so a
        // rapid A→B switch can't let A's refresh snap the selection back to A.
        if (activateSeqRef.current === seq) {
          void refreshRecords();
          void reinitContext();
        }
      })();
      return true;
    },
    [enabled, query, refreshRecords, reinitContext]
  );

  const saveRecord = useCallback(
    async (id: string): Promise<boolean> => {
      try {
        // Saving commits the draft's on-device picks to Cosmos, then flips the
        // status. If it's the active ballot, the live store is authoritative —
        // sync it to the draft cache (grounded to this ballot) first.
        if (currentIdRef.current === id) {
          const ballotIds = currentBallotIdsRef.current;
          const storeIds = getLikedCandidateIds();
          setDraftSelections(
            id,
            ballotIds.size ? storeIds.filter((cid) => ballotIds.has(cid)) : storeIds
          );
          const ballotMeasureIds = currentBallotMeasureIdsRef.current;
          const storeMeasures = getMeasureSelections();
          setDraftMeasureSelections(
            id,
            ballotMeasureIds.size
              ? Object.fromEntries(
                  Object.entries(storeMeasures).filter(([mid]) => ballotMeasureIds.has(mid))
                )
              : storeMeasures
          );
        }
        // Only push selections we actually hold on this device. A non-active
        // draft with no local cache keeps its server selections — a full-replace
        // PUT with an empty payload would otherwise wipe picks made elsewhere. When
        // only one of candidates/measures has a local cache, the other falls back
        // to the last-known server value (this is a full-replace PUT of both) so
        // it isn't wiped either.
        const cachedPicks = getDraftSelections(id);
        const cachedMeasures = getDraftMeasureSelections(id);
        if (cachedPicks !== null || cachedMeasures !== null) {
          const lastKnown = recordCacheRef.current.get(id);
          await putSelections(
            id,
            cachedPicks ?? lastKnown?.selections?.candidates ?? [],
            cachedMeasures ?? lastKnown?.selections?.measures ?? {}
          );
        }

        const response = await query(`/user/ballots/${id}`, {
          method: 'PATCH',
          body: JSON.stringify({ status: 'saved' }),
        });
        const data = await response.json();
        if (!data.success || !data.ballot) return false;
        const ballot = parseRecord(data.ballot);
        // Now saved: status flips so future picks sync straight to Cosmos, and
        // the on-device draft copy is no longer the source of truth.
        if (currentIdRef.current === id) currentStatusRef.current = 'saved';
        clearDraftSelections(id);
        clearDraftMeasureSelections(id);
        setRecords((prev) => mergeIntoSummaries(prev, ballot));
        setActiveRecord((prev) => (prev?.id === id ? { ...prev, ...ballot } : prev));
        setViewedRecord((prev) =>
          prev?.id === id ? { ...prev, status: ballot.status, saved_at: ballot.saved_at } : prev
        );
        return true;
      } catch (error) {
        console.error('Ballot save failed:', error);
        return false;
      }
    },
    [query, putSelections]
  );

  // Toggle the per-ballot "Complete this ballot" override. On forces the green
  // complete state regardless of picks (and commits the ballot to saved); off
  // restores normal heart-selection completeness.
  const setManualCompletion = useCallback(
    async (id: string, complete: boolean): Promise<boolean> => {
      try {
        // Completing commits the ballot to saved, so flush this ballot's
        // on-device draft picks to Cosmos first (mirrors saveRecord) — otherwise
        // selections made in draft would be dropped from the saved record.
        const flushDraft =
          complete && currentIdRef.current === id && currentStatusRef.current === 'draft';
        if (flushDraft) {
          const ballotIds = currentBallotIdsRef.current;
          const storeIds = getLikedCandidateIds();
          const picks = ballotIds.size ? storeIds.filter((cid) => ballotIds.has(cid)) : storeIds;
          setDraftSelections(id, picks);
          const ballotMeasureIds = currentBallotMeasureIdsRef.current;
          const storeMeasures = getMeasureSelections();
          const votes = ballotMeasureIds.size
            ? Object.fromEntries(
                Object.entries(storeMeasures).filter(([mid]) => ballotMeasureIds.has(mid))
              )
            : storeMeasures;
          setDraftMeasureSelections(id, votes);
          await putSelections(id, picks, votes);
        }

        const response = await query(`/user/ballots/${id}/completion`, {
          method: 'PUT',
          body: JSON.stringify({ complete }),
        });
        const data = await response.json();
        if (!data.success || !data.ballot) return false;
        const ballot = parseRecord(data.ballot);
        if (currentIdRef.current === id) currentStatusRef.current = ballot.status ?? 'saved';
        if (flushDraft) {
          clearDraftSelections(id);
          clearDraftMeasureSelections(id);
        }
        setRecords((prev) => mergeIntoSummaries(prev, ballot));
        setActiveRecord((prev) => (prev?.id === id ? { ...prev, ...ballot } : prev));
        setViewedRecord((prev) =>
          prev?.id === id
            ? {
                ...prev,
                status: ballot.status,
                saved_at: ballot.saved_at,
                is_complete: ballot.is_complete,
                manually_complete: ballot.manually_complete,
              }
            : prev
        );
        return true;
      } catch (error) {
        console.error('Ballot manual-completion update failed:', error);
        return false;
      }
    },
    [query, putSelections]
  );

  const deleteRecord = useCallback(
    async (id: string): Promise<boolean> => {
      const wasActive = activeBallotId === id || currentIdRef.current === id;
      // Optimistic next active: the top remaining visible entry (newest address's
      // soonest-upcoming), which matches the backend's re-point choice.
      const optimisticNext = wasActive
        ? (orderedRecords.find((r) => r.id !== id && r.is_visible_entry !== false) ?? null)
        : null;

      // Optimistic UI: drop the row and switch the selection now — the DELETE
      // (which re-points server-side) then runs off the interaction path.
      setRecords((prev) => prev.filter((r) => r.id !== id));
      setViewedRecord((prev) => (prev?.id === id ? null : prev));
      recordCacheRef.current.delete(id);
      if (wasActive) {
        currentIdRef.current = null;
        if (optimisticNext) {
          setActiveBallotId(optimisticNext.id);
          setActiveRecord(recordCacheRef.current.get(optimisticNext.id) ?? null);
        } else {
          setActiveBallotId(null);
          setActiveRecord(null);
          ContextService.clearContext();
        }
      }

      try {
        const response = await query(`/user/ballots/${id}`, { method: 'DELETE' });
        const data = await response.json();
        if (!data.success) {
          await refreshRecords(); // rollback to server truth
          return false;
        }
        // Reconcile: correct the optimistic guess only when the backend re-pointed
        // to a different ballot than we assumed.
        if (data.active_changed) {
          const newActive = data.active_ballot ? parseRecord(data.active_ballot) : null;
          if ((newActive?.id ?? null) !== (optimisticNext?.id ?? null)) {
            currentIdRef.current = null;
            setViewedRecord(null);
            setActiveBallotId(newActive ? newActive.id : null);
            setActiveRecord(newActive);
          }
          if (newActive) {
            void refreshRecords();
            void reinitContext();
          } else {
            setRecords([]);
            ContextService.clearContext();
          }
        } else {
          void refreshRecords();
        }
        return true;
      } catch (error) {
        console.error('Ballot delete failed:', error);
        await refreshRecords(); // rollback to server truth
        return false;
      }
    },
    [query, refreshRecords, reinitContext, activeBallotId, orderedRecords]
  );

  const updateViewedSelections = useCallback(
    async (candidates: string[], measures?: Record<string, string>) => {
      const record = viewedRecord;
      if (!record) return;
      const nextMeasures = measures ?? record.selections?.measures ?? {};
      // Optimistic: reflect the heart/vote immediately, reconcile with response.
      setViewedRecord((prev) =>
        prev
          ? {
              ...prev,
              selections: {
                candidates,
                measures: nextMeasures,
              },
            }
          : prev
      );
      try {
        const ballot = await putSelections(record.id, candidates, nextMeasures);
        if (ballot) {
          setViewedRecord((prev) =>
            prev?.id === ballot.id
              ? {
                  ...prev,
                  selections: ballot.selections,
                  status: ballot.status,
                  is_complete: ballot.is_complete,
                }
              : prev
          );
        }
      } catch (error) {
        console.error('Ballot record selections update failed:', error);
      }
    },
    [viewedRecord, putSelections]
  );

  const syncAutoSaveEnabled = useCallback((enabled: boolean) => {
    setAutoSave(enabled);
  }, []);

  const setAutoSaveEnabled = useCallback(
    async (nextEnabled: boolean): Promise<boolean> => {
      if (!user?.email) return false;
      const previous = autoSaveEnabled;
      setAutoSave(nextEnabled);
      try {
        // Touch only the field this toggle owns, so it can't clobber newer
        // name/phone the user saved from the Settings page. The backend
        // skips PII fields that arrive as null. first_name/last_name default
        // to null when omitted, but phone_number defaults to "" server-side
        // (which would clear the stored number), so send it explicitly null.
        const response = await query('/user/settings', {
          method: 'POST',
          body: JSON.stringify({
            email: user.email,
            phone_number: null,
            auto_save_ballots: nextEnabled,
          }),
        });
        const data = await response.json();
        if (!data.success) throw new Error('settings update rejected');
        // Enabling auto-save promotes every currently-drafted ballot to saved
        // right away (the toggle means "keep all my ballots saved"), alongside
        // the settings write. Disabling only flips the preference.
        if (nextEnabled) {
          await Promise.all(
            records.filter((r) => r.status === 'draft').map((r) => saveRecord(r.id))
          );
        }
        return true;
      } catch (error) {
        console.error('Auto-save setting update failed:', error);
        setAutoSave(previous);
        return false;
      }
    },
    [autoSaveEnabled, query, user?.email, records, saveRecord]
  );

  const value = useMemo<BallotRecordsContextValue>(
    () => ({
      records: orderedRecords,
      recordsLoading,
      refreshRecords,
      activeBallotId,
      activateBallot,
      currentRecord,
      activeRecord,
      activeRecordError,
      building,
      beginBuilding,
      viewedRecord,
      viewedLoading,
      viewedError,
      viewRecord,
      clearViewedRecord,
      saveRecord,
      setManualCompletion,
      deleteRecord,
      updateViewedSelections,
      autoSaveEnabled,
      setAutoSaveEnabled,
      syncAutoSaveEnabled,
    }),
    [
      orderedRecords,
      recordsLoading,
      refreshRecords,
      activeBallotId,
      activateBallot,
      currentRecord,
      activeRecord,
      activeRecordError,
      building,
      beginBuilding,
      viewedRecord,
      viewedLoading,
      viewedError,
      viewRecord,
      clearViewedRecord,
      saveRecord,
      setManualCompletion,
      deleteRecord,
      updateViewedSelections,
      autoSaveEnabled,
      setAutoSaveEnabled,
      syncAutoSaveEnabled,
    ]
  );

  return <BallotRecordsContext.Provider value={value}>{children}</BallotRecordsContext.Provider>;
};
